<template>
    <div class="container mx-auto text-center max-w-lg lg:max-w-sm md:mt-5 lg:mt-0">
        <div class="md:border md:border-black bg-white py-5">
            <h2 class="pb-3" v-text="$t('PAYMENT_SUMMARY_FINAL_CHECKS')"></h2>

            <!-- Coupon Info -->
            <div class="pb-3" v-if="multiCoupon && isSubscription && validDiscount">
                <p class="font-light"> {{ multiGiftingText}}</p>
            </div>
            <div class="pb-3" v-if="singleCoupon && !isSubscription">
                <p class="font-light"> {{ singleGiftingText}}</p>
            </div>

            <!-- Send summary -->
            <div class="pb-3">
                {{ $t('PAYMENT_SUMMARY_SENDING') }}
                <div class="font-light" v-text="summaryString"></div>
            </div>

            <!-- Address -->
            <div class="pb-3">
                <div v-text="$t('PAYMENT_SUMMARY_SENDING_TO')"></div>
                <div class="font-light">
                    <div v-text="fullName"></div>
                    <div v-text="address.address1 + ' ' + address.house"></div>
                    <div v-if="address.address2 && address.address2.length" v-text="address.address2"></div>
                    <div v-text="address.postcode"></div>
                    <div v-text="address.city"></div>
                </div>
            </div>

            <!-- First Delivery -->
            <div class="pb-3">
                <div v-if="isSubscription" v-text="$t('PAYMENT_SUMMARY_STARTING_ON')"></div>
                <div v-else v-text="$t('PAYMENT_SUMMARY_DELIVERY_ON')"></div>
                <div class="font-light" v-html="formattedDeliveryDate"></div>
            </div>

            <!-- Final Delivery -->
            <div v-if="isSubscription" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_FINAL_DELIVERY') }}
                <div class="font-light" v-html="formattedLastDeliveryDate"></div>
            </div>

            <!-- Vase -->
            <div v-if="isVaseIncluded" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_ALONG_WITH') }}
                <div class="font-light" v-if="isVaseFree">
                    {{ $t('PAYMENT_SUMMARY_A_FREE_VASE') }}
                </div>
                <div class="font-light" v-else>
                    {{ $t('PAYMENT_SUMMARY_A_FAB_FREDDIES_VASE') }}
                </div>
            </div>

            <!-- Gift card -->
            <div class="pb-3">
                {{ $t('PAYMENT_SUMMARY_PERSONAL_NOTE') }}
                <div class="font-light" v-text="messageTo"></div>
                <div class="font-light" v-text="messageBody"></div>
                <div class="font-light" v-text="messageFrom"></div>
            </div>

            <!-- Notification number -->
            <div v-if="address.sends_sms" class="pb-3">
                <div v-text="notifierTitleString"></div>
                <div class="font-light" v-text="notificationMobileNumber"></div>
            </div>

            <!-- Summary -->
            <div class="pb-3">
                <div v-if="isSubscription">
                    {{ totalDeliveries }} {{ $t('PAYMENT_SUMMARY_BOXES') }}
                </div>
                <div v-else v-text="$t('PAYMENT_SUMMARY_ONE_OFF')"></div>
                <div class="font-light">{{$root.country.currency.symbol}}{{ deliveriesPrice }}</div>
            </div>

            <div v-if="isVaseIncluded" class="pb-3">
                {{ $t('PAYMENT_SUMMARY_FAB_VASE') }}
                <div class="font-light" v-if="isVaseFree" v-text="$t('PAYMENT_SUMMARY_FREE')"/>
                <div class="font-light" v-else>{{$root.country.currency.symbol}}{{ vasePrice }}</div>
            </div>

            <div v-for="(item, key) in additionalProducts" :key="key" class="pb-3">
                {{item.quantity}} x {{item.title}}
                <div class="font-light">{{currency}}{{item.price}}</div>
            </div>

            <!-- Total -->
            <div>
                {{ $t('PAYMENT_SUMMARY_TOTAL_PRICE') }}
                <div class="font-light">{{$root.country.currency.symbol}}{{ totalPrice }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'PaymentSummary',
    data() {
        return {
            dateFormat: 'Do MMMM YYYY',
        };
    },
    mounted() {
        this.$store.dispatch('gifting/fetchPrices');
        this.$store.dispatch('gifting/fetchGiftingCoupons');
    },
    methods: {
        calculateDiscountAmount() {
            const discountType = this.isSubscription ? this.multiCoupon.discountType : this.singleCoupon.discountType;
            const discountValue = this.isSubscription ? this.multiCoupon.discount : this.singleCoupon.discount;

            if (discountType === 'percentage') {
                const saving = this.totalDeliveriesPrice * (discountValue / 100);
                return saving.toFixed(2);
            } else if (discountType === 'amount') {
                const saving = discountValue * 0.01;
                return saving.toFixed(2);
            }
            return 0;
        },
        calculateTotalPrice() {
            const validSingleGiftDiscount = this.singleCoupon;
            const validMultiGiftDiscount = this.multiCoupon && this.validDiscount;
            const discountCriteria = this.isSubscription ? validMultiGiftDiscount : validSingleGiftDiscount;
            const totalPrice = discountCriteria ? this.discountedPrice : Number(this.totalDeliveriesPrice);

            if (this.isVaseIncluded && this.paidVaseSelected) {
                return totalPrice + Number(this.vasePrice);
            }
            return (this.additionalProductsCost + totalPrice).toFixed(2);
        },
        couponDiscount(coupon) {
            if (coupon.discountType === 'percentage') {
                return `${coupon.discount}%`;
            } else if (coupon.discountType === 'amount') {
                const saving = coupon.discount * 0.01;
                return `£${saving}`;
            }
        },
    },
    computed: {
        ...mapGetters({
            address: 'address/getSelectedGiftingAddress',
            deliveryDate: 'gifting/getDeliveryDateSelected',
            frequency: 'gifting/getFrequencyForSubscription',
            isSubscription: 'gifting/isSubscription',
            lastDeliveryDate: 'gifting/getSubscriptionPredictedLastDeliveryDate',
            messageBody: 'gifting/getMessageBody',
            messageFrom: 'gifting/getMessageFrom',
            messageTo: 'gifting/getMessageTo',
            multiCoupon: 'gifting/getMultiGiftCoupon',
            notificationMobileNumber: 'gifting/getNotifierNumber',
            notifier: 'gifting/getNotifier',
            numberOfMonths: 'gifting/getNumberOfMonthsForSubscription',
            paidVaseSelected: 'gifting/isPaidVaseAttached',
            singleCoupon: 'gifting/getSingleGiftCoupon',
            totalDeliveriesPrice: 'gifting/getTotalPriceForDeliveries',
            totalDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            vasePrice: 'gifting/getPaidVasePrice',
            additionalProducts: 'gifting/getAdditionalProducts',
            currency: 'localisation/getCurrency',
            additionalProductsCost: 'gifting/getAdditionalProductsTotalCost',
        }),
        fullName() {
            return `${this.address.first_name} ${this.address.last_name}`;
        },
        deliveriesPrice: function() {
            return Number(this.totalDeliveriesPrice).toFixed(2);
        },
        formattedDeliveryDate() {
            return this.$moment(this.deliveryDate).format(this.dateFormat);
        },
        formattedLastDeliveryDate() {
            return this.$moment(this.lastDeliveryDate).format(this.dateFormat);
        },
        isVaseEnabled() {
            return process.env.VUE_APP_MIX_GIFTING_ENABLE_VASE === 'true';
        },
        isVaseIncluded() {
            return this.isVaseEnabled && (this.isSubscription || this.paidVaseSelected);
        },
        isVaseFree() {
            return this.isVaseEnabled && this.isSubscription;
        },
        notifierTitleString() {
            return this.notifier.toLowerCase() === this.$t('PAYMENT_SUMMARY_YOU')
                ? this.$t('PAYMENT_SUMMARY_COURIER')
                : this.$t('PAYMENT_SUMMARY_COURIER_WILL', { 'recipientFirstName': this.$t(`${this.recipientFirstName}`) });
        },
        summaryString() {
            if (!this.isSubscription) {
                return this.$t('PAYMENT_SUMMARY_ONE_OFF_BOX');
            }
            let monthString = this.numberOfMonths === 1 ? this.$t('PAYMENT_SUMMARY_MONTH') : this.$t('PAYMENT_SUMMARY_MONTHS');
            let frequencyString = this.frequency.toLowerCase();
            return this.$t('PAYMENT_SUMMARY_OF_DELIVERIES', {
                'numberOfMonths': this.$t(`${this.numberOfMonths}`),
                'monthString': this.$t(`${monthString}`),
                'frequencyString': this.$t(`${frequencyString}`),
            });
        },
        totalPrice() {
            return this.calculateTotalPrice();
        },
        discountedPrice() {
            const saving = this.calculateDiscountAmount();
            const price = this.totalDeliveriesPrice - saving;
            return price;
        },
        validDiscount() {
            const minBoxes = this.multiCoupon.minimumWeeks;
            return this.totalDeliveries >= minBoxes;
        },
        singleGiftingText() {
            if (this.totalPrice === 0) {
                return this.$t('SINGLE_GIFT_COUPON_100');
            }
            return this.$t('SINGLE_GIFT_COUPON', { amount: this.couponDiscount(this.singleCoupon) });
        },
        multiGiftingText() {
            if (this.totalPrice === 0) {
                return this.$t('MULTI_GIFT_COUPON_100');
            }
            return this.$t('MULTI_GIFT_COUPON', { amount: this.couponDiscount(this.multiCoupon) });
        },
    },
};
</script>
